import cloneDeep from 'lodash/cloneDeep';
import { Signal } from 'typed-signals';
import type { BlockerModel, BlockerState, PlankBlockerState, BlockerType, BlockerMatchParams } from '../../types';
import type { TileStackModel } from '../board/TileStackModel';
import { generateId } from '../../../../utils/generateId';
import MathUtils from '../../../../utils/MathUtils';
import { TileColor } from '../../TileColor';
import type { TileModel } from '../board/TileModel';

export class PlankBlockerModel implements BlockerModel {
  public readonly id = generateId('plank');

  public readonly underTile = false;

  public readonly destructible = true;

  public readonly maxHp = 3;

  public readonly requestRemoval = new Signal();

  public readonly onModelChanged = new Signal();

  private readonly state: PlankBlockerState;

  constructor(
    private readonly stack: TileStackModel,
    state: PlankBlockerState,
  ) {
    this.state = cloneDeep(state);
  }

  public onMatch({ stack, tilesRemoved, distantMatch }: BlockerMatchParams): void {
    const isNeighbour = stack.getConnectedStacks().some((neighbourOfMatch) => neighbourOfMatch === this.stack);
    if (!isNeighbour && !distantMatch) return;

    if (this.shouldBeMatchBlockedByColor(tilesRemoved[0])) return;

    this.state.hp -= 1;
    if (this.state.hp <= 0) {
      this.requestRemoval.emit();
    } else {
      this.onModelChanged.emit();
    }
  }

  private shouldBeMatchBlockedByColor(tileModel?: TileModel): boolean {
    const {
      state: { color },
    } = this;
    if (color === undefined || color === TileColor.None) return false;
    if (!tileModel) return false;
    return tileModel.color !== color;
  }

  public getType(): BlockerType {
    return this.state.type;
  }

  public getHp(): number {
    return this.state.hp;
  }

  public getColor(): TileColor {
    return this.state.color ?? TileColor.None;
  }

  public setColor(color: TileColor): void {
    if (!Object.values(TileColor).includes(color)) return;
    this.state.color = color;
  }

  public setHp(hp: number): void {
    this.state.hp = MathUtils.clamp(hp, 0, this.maxHp);
    this.onModelChanged.emit();
  }

  public exportState(): BlockerState {
    return this.state;
  }
}
